import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { normalizeMatch } from '@/hooks/home/utils/swedishLeagueNormalizer';

const DAYS_LOOKBACK = 28;
const API_HOST = import.meta.env.VITE_API_URL;
const API_KEY = import.meta.env.VITE_API_KEY;

const extractMatches = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.matches)) return payload.matches;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const normalizePayload = (rawPayload = {}) => {
  const primaryMatches = extractMatches(rawPayload);
  const secondaryMatches = primaryMatches.length
    ? []
    : extractMatches(rawPayload?.data ?? rawPayload?.result);
  const matches = primaryMatches.length ? primaryMatches : secondaryMatches;

  return matches.map((match) =>
    normalizeMatch({ ...match, status: 'completed' })
  );
};

const createRequestUrl = (days = DAYS_LOOKBACK) => {
  return `${API_HOST}/swedishleague/lastdays/${days}`;
};

const buildRequestInit = (signal) => {
  const headers = new Headers({
    'Content-Type': 'application/json',
  });

  if (API_KEY) {
    headers.append('apiKey', API_KEY);
  }

  return {
    method: 'GET',
    headers,
    credentials: 'include',
    signal,
  };
};

export default function useCompletedSwedishMatches({
  days = DAYS_LOOKBACK,
} = {}) {
  const [completedMatches, setCompletedMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef();

  const fetchMatches = useCallback(
    async ({ withLoading = true, signal } = {}) => {
      if (withLoading) {
        setIsLoading(true);
      }
      setError(null);
      try {
        const response = await fetch(
          createRequestUrl(days),
          buildRequestInit(signal)
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch Swedish league matches (${response.status})`
          );
        }
        const payload = await response.json();
        const normalized = normalizePayload(payload);
        setCompletedMatches(normalized);
      } catch (fetchError) {
        if (fetchError.name === 'AbortError') {
          return;
        }
        console.error('useCompletedSwedishMatches', fetchError);
        setError(fetchError);
        setCompletedMatches([]);
      } finally {
        if (withLoading) {
          setIsLoading(false);
        }
      }
    },
    [days]
  );

  const refresh = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const controller = new AbortController();
    abortRef.current = controller;
    fetchMatches({ withLoading: true, signal: controller.signal });
  }, [fetchMatches]);

  useEffect(() => {
    refresh();
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [refresh]);

  const meta = useMemo(
    () => ({
      isLoading,
      hasError: Boolean(error),
      error,
      refresh,
    }),
    [error, isLoading, refresh]
  );

  return {
    completedMatches,
    ...meta,
  };
}
